import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthAPI, FeedbackAPI, resolveAsset } from "../../services/api";
import { getUserFromToken } from "../../services/authService";
import { feedbackMockData } from "./feedbackMockData";

const categoryPill = (tone) => {
  switch (tone) {
    case "orange":
      return "bg-orange-100 text-orange-700";
    case "amber":
      return "bg-amber-100 text-amber-700";
    case "blue":
      return "bg-blue-100 text-blue-700";
    case "purple":
      return "bg-violet-100 text-violet-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
};

function parseMaybeJson(value) {
  if (!value || typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getFeedbackClosure(feedback) {
  const closure = parseMaybeJson(feedback?.closure);
  const reportedClosure = parseMaybeJson(feedback?.reportedClosure);
  const closureReview = parseMaybeJson(feedback?.closureReview);
  const reported =
    closure ||
    reportedClosure ||
    closureReview;

  if (reported) return reported;

  const floorId = feedback?.closureFloorId ?? feedback?.closure_floor_id;
  const x = feedback?.closureX ?? feedback?.closure_x;
  const y = feedback?.closureY ?? feedback?.closure_y;
  if (floorId == null || x == null || y == null) return null;
  return {
    floorId,
    x: Number(x),
    y: Number(y),
    radiusPx: Number(feedback?.closureRadiusPx ?? feedback?.closure_radius_px ?? 80),
    status: feedback?.closureStatus || "pending",
  };
}

function isClosedRouteCategory(category) {
  if (!category) return false;
  const lower = String(category).toLowerCase().replace(/\s/g, "");
  return lower === "ทางปิด" || lower === "closedroute";
}

export default function FeedbackDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const fallbackFeedback =
    feedbackMockData.find((item) => String(item.id) === String(id)) || null;

  const [feedback, setFeedback] = useState(
    location.state?.feedback || fallbackFeedback,
  );
  const [status, setStatus] = useState(
    (location.state?.feedback || fallbackFeedback)?.status || "pending",
  );
  const [staffProfile, setStaffProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [closureActionSaving, setClosureActionSaving] = useState("");

  useEffect(() => {
    FeedbackAPI.get(id)
      .then((data) => {
        if (!data) return;
        setFeedback((prev) => ({ ...(prev || {}), ...data }));
        setStatus(data.status || "pending");
      })
      .catch(() => { });
  }, [id]);

  useEffect(() => {
    const tokenUser = getUserFromToken();
    if (tokenUser) {
      setStaffProfile(tokenUser);
    }

    AuthAPI.me()
      .then((profile) => setStaffProfile(profile?.user || profile))
      .catch(() => setStaffProfile(tokenUser || null));
  }, []);

  const onConfirm = async () => {
    if (!feedback) return;
    const previousStatus = feedback.status || "pending";
    setSaving(true);
    try {
      const updated = await FeedbackAPI.setStatus(feedback.id, status);
      const nextFeedback = {
        ...feedback,
        ...(updated || {}),
        status,
        handledByName:
          staffProfile?.name ||
          staffProfile?.fullName ||
          staffProfile?.email ||
          feedback.handledByName,
        handledByEmail: staffProfile?.email || feedback.handledByEmail,
      };
      setFeedback(nextFeedback);
      window.dispatchEvent(
        new CustomEvent("feedback-status-updated", {
          detail: {
            id: nextFeedback.id,
            previousStatus,
            status,
            staff: staffProfile,
            feedback: nextFeedback,
          },
        })
      );
      navigate("/admin/feedback", {
        state: {
          updatedFeedback: nextFeedback,
          previousStatus,
        },
      });
    } catch (e) {
      alert(e.message || t("admin.feedbackDetail.saveStatusError"));
    } finally {
      setSaving(false);
    }
  };

  const normalizeFeedbackResponse = (data, fallbackStatus) => {
    const next = data?.feedback || data?.item || data || {};
    const nextClosure =
      parseMaybeJson(next.closure) ||
      parseMaybeJson(next.reportedClosure) ||
      parseMaybeJson(data?.closure) ||
      parseMaybeJson(data?.reportedClosure) ||
      getFeedbackClosure(feedback);
    return {
      ...feedback,
      ...next,
      closure: nextClosure
        ? {
          ...(feedback.closure || {}),
          ...(nextClosure || {}),
          ...(fallbackStatus ? { status: fallbackStatus } : {}),
        }
        : feedback.closure,
    };
  };

  const onConfirmClosure = async () => {
    if (!feedback?.id) return;
    setClosureActionSaving("confirm");
    try {
      const updated = await FeedbackAPI.confirmClosure(feedback.id);
      setFeedback(normalizeFeedbackResponse(updated, "active"));
      setStatus("pending");
    } catch (e) {
      alert(e.message || "ยืนยันทางปิดไม่สำเร็จ");
    } finally {
      setClosureActionSaving("");
    }
  };

  const onRejectClosure = async () => {
    if (!feedback?.id) return;
    setClosureActionSaving("reject");
    try {
      const updated = await FeedbackAPI.rejectClosure(feedback.id);
      setFeedback(normalizeFeedbackResponse(updated, "rejected"));
      setStatus("rejected");
    } catch (e) {
      alert(e.message || "ปฏิเสธทางปิดไม่สำเร็จ");
    } finally {
      setClosureActionSaving("");
    }
  };

  const getLocalizedCategory = (cat) => {
    if (!cat) return "";
    const lower = cat.toLowerCase();
    if (lower === "ตำแหน่งไม่ตรง" || lower === "locationmismatch") return t("user.feedback.problems.locationMismatch");
    if (lower === "ห้องย้ายที่" || lower === "roommoved") return t("user.feedback.problems.roomMoved");
    if (lower === "ทางปิด" || lower === "closedroute") return t("user.feedback.problems.closedRoute");
    if (lower === "อื่นๆ" || lower === "อื่น ๆ" || lower === "other" || lower === "others") return t("user.feedback.problems.other");
    if (lower === "ห้องน้ำ" || lower === "bathroom") return t("user.layout.restroom");
    if (lower === "ทางเข้า" || lower === "entrance") return t("admin.navigation.drawingLine"); // Fallback fallback
    return cat;
  };

  if (!feedback) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="text-slate-900 font-black">{t("admin.feedbackDetail.notFound")}</div>
        <button
          onClick={() => navigate("/admin/feedback")}
          className="mt-4 px-4 py-2 rounded-xl bg-blue-600 text-white font-bold"
        >
          {t("admin.feedbackDetail.backToFeedback")}
        </button>
      </div>
    );
  }

  const staffName =
    staffProfile?.name ||
    staffProfile?.fullName ||
    feedback.handledByName ||
    feedback.staffName ||
    t("admin.feedbackDetail.staff");
  const staffEmail =
    staffProfile?.email ||
    feedback.handledByEmail ||
    feedback.staffEmail ||
    "-";
  const displayClosure = getFeedbackClosure(feedback);
  const isClosedRouteFeedback = isClosedRouteCategory(feedback.cat || feedback.category);
  const closureStatus = String(displayClosure?.status || "pending").toLowerCase();
  const isClosurePending =
    !!displayClosure && ["pending", "reported", "review", "reviewing"].includes(closureStatus);
  const isClosureActive = !!displayClosure && closureStatus === "active";
  const isClosureResolved = !!displayClosure && closureStatus === "resolved";
  const isClosureRejected =
    !!displayClosure && ["rejected", "cancelled", "canceled"].includes(closureStatus);
  const canUseFeedbackResolutionFlow =
    !isClosedRouteFeedback || isClosureActive || isClosureResolved;
  const closureStatusLabel = isClosureResolved
    ? "แก้ไขแล้ว — แสดงเส้นทางปกติ"
    : isClosureRejected
      ? "ไม่ใช่ทางปิด — ไม่เลี่ยงเส้นทาง"
      : isClosureActive
        ? "ยืนยันแล้ว — ระบบเลี่ยงเส้นทางนี้อยู่"
        : "รอตรวจสอบจากเจ้าหน้าที่";
  const closureStatusClass = isClosureResolved
    ? "text-emerald-600"
    : isClosureRejected
      ? "text-slate-500"
      : isClosureActive
        ? "text-red-600"
        : "text-amber-600";
  const detailHelpText = isClosurePending
    ? "กรุณาตรวจสอบก่อนว่าทางนี้ปิดจริงหรือไม่ ถ้าไม่จริงให้ปฏิเสธและจบรายการนี้ได้เลย"
    : isClosureActive
      ? "ทางปิดนี้ถูกแสดงบนแผนที่ user แล้ว และระบบกำลังเลี่ยงเส้นทางนี้อยู่"
      : isClosureRejected
        ? "รายการนี้ถูกปฏิเสธแล้ว จึงไม่แสดงบนแผนที่ user และไม่กระทบการนำทาง"
        : isClosedRouteFeedback && !displayClosure
          ? "กำลังโหลดข้อมูลตำแหน่งทางปิดจากระบบ หากข้อความนี้ไม่หายไป แปลว่า backend ยังไม่ได้ส่ง closure มากับ feedback รายการนี้"
          : "";

  return (
    <div className="font-sans">
      <div className="mb-4">
        <div className="flex items-center gap-2 text-slate-900">
          <button
            type="button"
            onClick={() => navigate("/admin/feedback")}
            className="text-4xl leading-none font-light hover:text-blue-600"
            aria-label={t("admin.feedbackDetail.back")}
          >
            ‹
          </button>
          <span className="text-xl font-semibold">
            {t("admin.feedbackDetail.back")}
          </span>
        </div>

        <p className="mt-2 text-sm text-slate-500">
          {t("admin.feedbackDetail.realUserFeedbackDescription")}
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
        <div className="max-w-[760px] mx-auto">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-full border-4 border-blue-100 flex items-center justify-center text-blue-600 font-black">
                ⊘
              </div>
              <div>
                <div className="text-slate-400 font-semibold">
                  {feedback.date}, {feedback.time}
                </div>
                <h1 className="text-[20px] font-black text-slate-900 mt-1">
                  {feedback.title || feedback.description}
                </h1>
              </div>
            </div>

            <span className={`text-xs font-black px-4 py-2 rounded-full ${categoryPill(feedback.catTone)}`}>
              {getLocalizedCategory(feedback.cat || feedback.category)}
            </span>
          </div>

          {(feedback.img || feedback.imageUrl) && (
            <div className="mt-5">
              <img
                src={resolveAsset(feedback.img || feedback.imageUrl)}
                alt={feedback.title || ""}
                className="w-full h-[360px] object-cover rounded-2xl border border-slate-200"
              />
            </div>
          )}

          {/* 🚧 ตำแหน่งทางปิด (ถ้ามี) */}
          {displayClosure && (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🚧</span>
                <div className="flex-1">
                  <div className="font-black text-red-700">
                    ตำแหน่งทางปิดที่ผู้ใช้แจ้ง
                  </div>
                  <div className="text-xs text-slate-600">
                    {displayClosure.buildingName || ""}
                    {displayClosure.floorName
                      ? ` • ${displayClosure.floorName}`
                      : ""}{" "}
                    (X: {Math.round(displayClosure.x)}, Y:{" "}
                    {Math.round(displayClosure.y)})
                  </div>
                  <div className="text-xs mt-1">
                    <span className="font-semibold">สถานะ:</span>{" "}
                    <span className={closureStatusClass}>
                      {closureStatusLabel}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {isClosurePending
                      ? "ทางปิดนี้ยังไม่แสดงบนแผนที่ และระบบยังไม่เลี่ยงเส้นทางจนกว่าเจ้าหน้าที่จะยืนยัน"
                      : isClosureActive
                        ? "เมื่อกด \"แก้ไขแล้ว\" สถานะของทางปิดจะถูกปลดอัตโนมัติ และ user จะนำทางผ่านเส้นทางนี้ได้ปกติ"
                        : "รายการนี้ไม่ถูกส่งออกเป็นทางปิดที่ active สำหรับ user"}
                  </div>
                  {isClosurePending && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={onConfirmClosure}
                        disabled={!!closureActionSaving}
                        className="rounded-xl bg-red-600 px-4 py-2 text-sm font-black text-white disabled:opacity-60"
                      >
                        {closureActionSaving === "confirm" ? "กำลังยืนยัน..." : "ยืนยันว่าเป็นทางปิด"}
                      </button>
                      <button
                        type="button"
                        onClick={onRejectClosure}
                        disabled={!!closureActionSaving}
                        className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-black text-slate-600 disabled:opacity-60"
                      >
                        {closureActionSaving === "reject" ? "กำลังปฏิเสธ..." : "ไม่ใช่ทางปิด"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {detailHelpText && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600">
              {detailHelpText}
            </div>
          )}

          {canUseFeedbackResolutionFlow && (
            <>
              <div className="mt-4">
                <div className="text-sm font-black text-slate-500">
                  {t("admin.feedbackDetail.feedbackStatusContext")}
                </div>

                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    onClick={() => setStatus("pending")}
                    className={
                      "rounded-xl py-3 font-black border " +
                      (status === "pending"
                        ? "bg-red-500 text-white border-red-500"
                        : "bg-white text-slate-400 border-slate-200")
                    }
                  >
                    {t("admin.feedbackDetail.unresolved")}
                  </button>

                  <button
                    onClick={() => setStatus("done")}
                    className={
                      "rounded-xl py-3 font-black border " +
                      (status === "done"
                        ? "bg-emerald-500 text-white border-emerald-500"
                        : "bg-white text-slate-400 border-slate-200")
                    }
                  >
                    {t("admin.feedbackDetail.resolved")}
                  </button>
                </div>
              </div>

              <div className="mt-5">
                <div className="text-sm font-black text-slate-500 mb-2">{t("admin.feedbackDetail.staffReceiver")}</div>
                <div className="max-w-[320px] border border-slate-200 rounded-2xl p-3 flex items-center gap-3 bg-slate-50">
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-black shrink-0">
                    {(staffEmail || staffName || "A").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-black text-slate-900">{staffName}</div>
                    <div className="text-sm text-slate-400">{staffEmail}</div>
                  </div>
                </div>
              </div>

              <button
                onClick={onConfirm}
                disabled={saving}
                className="mt-5 w-full rounded-xl py-4 bg-blue-600 text-white font-black flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <span>✔</span>
                <span>{saving ? t("admin.feedbackDetail.saving") : t("admin.feedbackDetail.confirmInfo")}</span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mt-6 text-center text-xs text-slate-400">
        {t("admin.feedbackDetail.copyright")}
      </div>
    </div>
  );
}
